import { type MouseEvent, useEffect } from 'react'
import type { DetectedMeeting, GoogleMeetMeeting, RecallDetectedMeeting } from '@highlight/common'
import { Toast, useToast } from '../../index'
import { Button } from '@highlight/ui'
import { useMissingAudioPermissions } from '@renderer/hooks/settings/useMissingAudioPermissions'
import { screenRecordingActionLabel, systemAudioActionLabel } from '@renderer/hooks/settings/audio-permission-prompt'
import WindowIpcRenderer from '@renderer/ipc/WindowIpc.renderer'
import { getDetectedMeetingAppName } from '@shared/utils/audio-notes/AudioNoteUtils'
import {
  AUDIO_PERMISSION_TOAST_SETTINGS_FALLBACK_MS,
  audioPermissionsSettingsRoute,
} from '@shared/settings/settings-deeplink'
import type { CapturePlan } from '@shared/store/impl/audioSettings'

interface AudioPermissionToastProps {
  meeting?: DetectedMeeting | GoogleMeetMeeting | RecallDetectedMeeting
  plan: CapturePlan
  toastId: string
}

/**
 * Shown when a meeting is detected but the macOS permissions a capture needs
 * aren't granted. Surfaces an inline grant button for each missing permission
 * (microphone, then CATAP system audio or Screen Recording depending on
 * runtime). The permission hooks poll, so granting from here — or from
 * anywhere — clears the toast and resumes the parked recording.
 *
 * If the permissions are still missing after a short delay, it escalates to
 * Settings → Meetings (the unified banner). Its overall lifespan is the shared
 * abort budget (see use-meeting-toasts), matching the main-process capture wait.
 */
export function AudioPermissionToast({ meeting, plan, toastId }: AudioPermissionToastProps) {
  const toast = useToast()
  const meetingAppName = meeting ? getDetectedMeetingAppName(meeting) : undefined
  const { mic, systemAudio, screenRecording, isLoaded, micMissing, nextPermission } = useMissingAudioPermissions({
    meetingAppName,
    plan,
  })

  const showSystemAudioPrompt = nextPermission === 'systemAudio'
  const showScreenRecordingPrompt = nextPermission === 'screenRecording'
  const stillWaiting = !isLoaded || nextPermission !== null

  // Everything this capture needs is granted — the parked meeting resumes in
  // main; drop the toast.
  useEffect(() => {
    if (!stillWaiting) {
      toast.dismiss(toastId)
    }
  }, [stillWaiting, toast, toastId])

  // Escalate to the Settings banner if still waiting after the fallback delay.
  // Re-runs as permissions land: once nothing is missing the timer is cleared,
  // so a grant before the delay cancels the pending Settings open.
  useEffect(() => {
    if (!stillWaiting) {
      return
    }
    const id = setTimeout(() => {
      void WindowIpcRenderer.openSettingsWindow(audioPermissionsSettingsRoute(meetingAppName, plan))
    }, AUDIO_PERMISSION_TOAST_SETTINGS_FALLBACK_MS)
    return () => clearTimeout(id)
  }, [meetingAppName, plan, stillWaiting])

  const message = !isLoaded
    ? 'Checking required permissions'
    : micMissing
      ? 'Microphone access is needed to transcribe'
      : showScreenRecordingPrompt
        ? 'Screen recording access is needed to transcribe'
        : 'System audio access is needed to transcribe'

  const grant = (request: () => Promise<void>) => (e: MouseEvent) => {
    e.stopPropagation()
    void request()
  }

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        {message}
        <Toast.Footer>
          <div className="flex items-center gap-2">
            {micMissing && (
              <Button onClick={grant(mic.request)} size="sm" variant="primary">
                Allow Microphone
              </Button>
            )}
            {showSystemAudioPrompt && (
              <Button onClick={grant(systemAudio.request)} size="sm" variant="primary">
                {systemAudioActionLabel(systemAudio.actionKind, 'Allow System Audio')}
              </Button>
            )}
            {showScreenRecordingPrompt && (
              <Button onClick={grant(screenRecording.request)} size="sm" variant="primary">
                {screenRecordingActionLabel(screenRecording.actionKind, 'Allow Screen Recording')}
              </Button>
            )}
          </div>
        </Toast.Footer>
      </Toast.Content>
    </Toast>
  )
}
